import type * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type InlineEditTextFieldStatus } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import messages from './messages'
import { useMutateInlineEditCreditCardItem } from '../../models'

const RESOLVED_DISPLAY_DURATION_MS = 2000

export interface IUseInlineEditCreditCardItemPresenterParams {
  item: CreditCardItem
  /** FS clears the suggested filter; invalidation refetches both panes (BHV-038) */
  onSaved: () => void
}

export const useInlineEditCreditCardItemPresenter = ({
  item,
  onSaved,
}: IUseInlineEditCreditCardItemPresenterParams) => {
  const intl = useProcurifyIntl()
  const { updateCostAndCurrency } = useMutateInlineEditCreditCardItem()

  const [inputValue, setInputValue] = useState(item.totalCost.toFixed(2))
  const [status, setStatus] = useState<InlineEditTextFieldStatus>('idle')
  const isMounted = useRef(true)
  const resolvedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (resolvedTimeoutRef.current) {
        clearTimeout(resolvedTimeoutRef.current)
        resolvedTimeoutRef.current = null
      }
    }
  }, [])

  // External list refresh (React Query refetch) re-syncs the displayed cost.
  useEffect(() => {
    setInputValue(item.totalCost.toFixed(2))
  }, [item.totalCost])

  const persist = useCallback(
    async (totalCost: string, currencyId: number) => {
      setStatus('pending')
      try {
        await updateCostAndCurrency({ id: item.id, totalCost, currencyId })
        if (!isMounted.current) return

        setStatus('resolved')
        onSaved()

        resolvedTimeoutRef.current = setTimeout(() => {
          if (!isMounted.current) return
          setStatus('idle')
          resolvedTimeoutRef.current = null
        }, RESOLVED_DISPLAY_DURATION_MS)
      } catch (error) {
        if (!isMounted.current) return

        setStatus('rejected')
        setInputValue(item.totalCost.toFixed(2))

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children:
            getMessageFromError(error) ??
            intl.formatMessage(messages.genericErrorMessage),
        })
      }
    },
    [intl, item.id, item.totalCost, onSaved, updateCostAndCurrency]
  )

  const handleCommit = useCallback(() => {
    const parsed = parseFloat(inputValue)

    if (Number.isNaN(parsed)) {
      setInputValue(item.totalCost.toFixed(2))
      return
    }

    const normalized = parsed.toFixed(2)
    setInputValue(normalized)

    if (normalized === item.totalCost.toFixed(2)) return

    persist(normalized, item.currency.id)
  }, [inputValue, item.currency.id, item.totalCost, persist])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleCommit()
      } else if (event.key === 'Escape') {
        setInputValue(item.totalCost.toFixed(2))
        setStatus('idle')
      }
    },
    [handleCommit, item.totalCost]
  )

  const handleCurrencyChange = useCallback(
    (currency: Currency) => {
      if (currency.id === item.currency.id) return
      persist(item.totalCost.toFixed(2), currency.id)
    },
    [item.currency.id, item.totalCost, persist]
  )

  return {
    data: {
      inputValue,
      status,
    },
    methods: {
      onInputChange: setInputValue,
      onInputBlur: handleCommit,
      onInputKeyDown: handleKeyDown,
      onCurrencyChange: handleCurrencyChange,
    },
  }
}
